import { Component, EventEmitter, Input, Output, SimpleChanges } from "@angular/core";
import { ITimelineEntry } from "@app/directives/_veri/v-timeline/v-timeline.component";
import { IGoogleMapsMarker } from "@app/interfaces/IGoogleMapsMarkerOptions";
import { VideoStatus } from "@app/main/components/videos/enums/VideoStatus";
import { IVideoEntry } from "@app/main/components/videos/models/IVideoEntry";
import { VideoEntryService } from "@app/main/components/videos/services/video-entry.service";
import { VideoEntryUtils } from "@app/main/components/videos/utils/video-entry-utils";
import { GeocodeService } from "@app/services/geocode.service";
import { MapService } from "@app/services/map.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { timer } from "rxjs";
import { RequestStep } from "../video-download-progress/video-download-progress.component";
import './video-request-status.component.scss';

@UntilDestroy()
@Component({
    selector: 'video-request-status',
    template: require('./video-request-status.component.html')
})
export class VideoRequestStatusComponent {
    @Input() videoEntry: IVideoEntry;
    @Output() videoOpen: EventEmitter<IVideoEntry> = new EventEmitter<IVideoEntry>();
    @Output() downloadComplete: EventEmitter<void> = new EventEmitter<void>();

    //--

    videoMarker: IGoogleMapsMarker;
    downloadedChannelsCount: number;
    currentStep: number;
    detailedStatus: string = this.translationService.t("VideoRequestStatus.VideoRequested");
    videosAvailable: boolean;
    statusIcon: string = 'downloading';
    showProgressBar = true;
    timelineEntries: ITimelineEntry[] = [];
    auditEntries: ITimelineEntry[] = [];

    VideoStatus = VideoStatus;
    loadingLocation: boolean;

    constructor(
        private readonly geocodeService: GeocodeService,
        private readonly mapService: MapService,
        private readonly translationService: TranslationService,
        private readonly videoEntryService: VideoEntryService) { }


    ngOnInit(): void {
        this.loadData();
        timer(0, 20_000)
            .pipe(untilDestroyed(this))
            .subscribe(() => this.updateVideoEntry());
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.getDownloadedChannelsCount();
    }


    openDownloadedVideo(): void {
        this.videoOpen.emit(this.videoEntry);
    }

    onStatusChanged(status: RequestStep): void {
        this.updateDetailedStatus(status);
        this.updateVideoEntry();
    }

    //--

    private loadData(): void {
        this.createMarkerFromVideoLocation();
        this.getDownloadedChannelsCount();
        this.updateVideoEntry();
        this.checkForErrors();
    }

    private updateVideoEntry(): void {
        this.videoEntryService.getVideoEntry(this.videoEntry.Id).then(videoEntry => {
            this.videoEntry = {
                ...this.videoEntry,
                ExternalId: videoEntry.ExternalId

            }
        });
    }


    private createMarkerFromVideoLocation(): void {
        if (!this.videoEntry.Latitude || !this.videoEntry.Longitude) {
            this.videoMarker = null;
            return;
        }
        let label: string = "";

        this.loadingLocation = true;

        this.geocodeService.getLocationInformationFromVideo(this.videoEntry.Id).then(location => {
            label = location.LocationDescription;
            this.videoMarker = this.mapService.createMapMarker({
                Latitude: this.videoEntry.Latitude,
                Longitude: this.videoEntry.Longitude
            }, { showLabel: true, label: label })
        }).finally(() => {
            this.loadingLocation = false;
        });
    }

    private getDownloadedChannelsCount(): void {
        if (!this.videoEntry) {
            return;
        }

        this.downloadedChannelsCount = 0;

        this.videoEntry.channelMedia.forEach(channel => {
            if (channel.mp4VideoUrl) {
                this.downloadedChannelsCount++;
            }
        })
    }

    private updateDetailedStatus(status: RequestStep): void {
        this.detailedStatus = this.translationService.t("VideoRequestStatus.VideoRequested");
        this.showProgressBar = true;

        switch (status) {
            case RequestStep.Requested:
                this.detailedStatus = this.translationService.t("VideoRequestStatus.VideoRequested");
                break;

            case RequestStep.Retrieving:
                this.detailedStatus = this.translationService.t("VideoRequestStatus.UploadingFootage");
                this.statusIcon = "upload_file";
                break;

            case RequestStep.Downloading:
                this.detailedStatus = this.translationService.t("VideoRequestStatus.DownloadingFootage");
                this.statusIcon = "downloading";
                break;

            case RequestStep.Complete:
                this.detailedStatus = this.translationService.t("VideoRequestStatus.VideosAvailable");
                this.statusIcon = "check_circle";
                this.downloadComplete.emit();
                break;

            case RequestStep.Faceblurring:
                this.detailedStatus = this.translationService.t("VideoRequestStatus.Faceblurring");
                this.statusIcon = "published_with_changes";
                break;
        }

        this.checkForErrors();
    }

    private checkForErrors(): void {

        if (VideoEntryUtils.hasNoFootage(this.videoEntry)) {
            this.detailedStatus = this.translationService.t("VideoRequestStatus.NoFootageOrFiles");
            this.statusIcon = "cloud_off";
            this.showProgressBar = false;
            return;
        }

        if (VideoEntryUtils.isDownloadFailed(this.videoEntry)) {
            this.detailedStatus = this.translationService.t("VideoRequestStatus.ErrorGettingVideos");
            this.statusIcon = "highlight_off";
            this.showProgressBar = false;
            return;
        }

        if (!this.videoEntry.vehicle?.onlineStatus?.online && !VideoEntryUtils.isRequestComplete(this.videoEntry)) {
            this.detailedStatus = this.translationService.t("VideoRequestStatus.WaitingForCamera");
            this.statusIcon = "videocam_off";
            this.showProgressBar = true;
            return;
        }
    }
}
